import React, { useState, useEffect } from 'react';
import { X, Clock, AlertTriangle, Calendar, Users, Search, Plus, Save, Target, FileText, DollarSign, Plane, GraduationCap } from 'lucide-react';
import { Deadline } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { mockStudents } from '../../data/mockData';
import { allUniversities } from '../../utils/consultantMatcher';
import { supabase } from '../../lib/supabase';

interface AddDeadlineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (deadlineData: any) => void;
}

interface Profile {
  id: string;
  name: string;
  email: string;
  role: string;
  unit_type: string | null;
  is_active: boolean;
  branch_id?: string;
}

const AddDeadlineModal: React.FC<AddDeadlineModalProps> = ({ isOpen, onClose, onSave }) => {
  const { user } = useAuth();
  const [dbUsers, setDbUsers] = useState<Profile[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'application' as Deadline['type'],
    dueDate: '',
    priority: 'medium' as Deadline['priority'],
    category: 'university' as Deadline['category'],
    affectedUsers: [] as string[],
    reminderDays: [30, 14, 7, 3, 1] as number[],
    relatedUniversities: [] as string[],
    relatedCountries: ['İtalya'] as string[],
    notes: '',
    contactPerson: user?.name || '',
    contactEmail: user?.email || ''
  });

  const [searchUsers, setSearchUsers] = useState('');
  const [searchUniversities, setSearchUniversities] = useState('');
  const [newReminderDay, setNewReminderDay] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Fetch users from database
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const { data, error } = await supabase
          .from('profiles')
          .select('id, name, email, role, unit_type, is_active, branch_id')
          .eq('is_active', true)
          .order('name');

        if (error) {
          console.error('Error fetching users:', error);
          setDbUsers([]);
        } else {
          setDbUsers(data || []);
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setDbUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const filteredUsers = dbUsers.filter(u =>
    u.name.toLowerCase().includes(searchUsers.toLowerCase()) ||
    u.email.toLowerCase().includes(searchUsers.toLowerCase())
  );



  const filteredUniversities = allUniversities.filter(uni =>
    uni.toLowerCase().includes(searchUniversities.toLowerCase())
  );

  const addUser = (userId: string, userName: string) => {
    if (!formData.affectedUsers.includes(userId)) {
      setFormData(prev => ({
        ...prev,
        affectedUsers: [...prev.affectedUsers, userId]
      }));
    }
    setSearchUsers('');
  };

  const removeUser = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      affectedUsers: prev.affectedUsers.filter(id => id !== userId)
    }));
  };



  const addUniversity = (university: string) => {
    if (!formData.relatedUniversities.includes(university)) {
      setFormData(prev => ({
        ...prev,
        relatedUniversities: [...prev.relatedUniversities, university]
      }));
    }
    setSearchUniversities('');
  };

  const removeUniversity = (university: string) => {
    setFormData(prev => ({
      ...prev,
      relatedUniversities: prev.relatedUniversities.filter(u => u !== university)
    }));
  };



  const addReminderDay = () => {
    const day = parseInt(newReminderDay);
    if (day > 0 && !formData.reminderDays.includes(day)) {
      setFormData(prev => ({
        ...prev,
        reminderDays: [...prev.reminderDays, day].sort((a, b) => b - a)
      }));
      setNewReminderDay('');
    }
  };

  const removeReminderDay = (day: number) => {
    setFormData(prev => ({
      ...prev,
      reminderDays: prev.reminderDays.filter(d => d !== day)
    }));
  };

  const addAllByRole = (role: string) => {
    const roleUsers = dbUsers.filter(u => u.role === role || u.unit_type === role);
    const newUserIds = roleUsers.map(u => u.id).filter(id => !formData.affectedUsers.includes(id));
    setFormData(prev => ({
      ...prev,
      affectedUsers: [...prev.affectedUsers, ...newUserIds]
    }));
  };

  const addBranchSalesTeam = () => {
    if (!user?.branchId) {
      alert('Şube bilgisi bulunamadı. Lütfen yöneticinizle iletişime geçin.');
      console.log('User object:', user);
      return;
    }
    console.log('Looking for sales team in branch:', user.branchId);
    console.log('Available users:', dbUsers);

    const branchSalesUsers = dbUsers.filter(u => {
      const isSales = u.unit_type === 'sales';
      const sameBranch = u.branch_id === user.branchId;
      console.log(`User ${u.name}: sales=${isSales}, sameBranch=${sameBranch}, branch_id=${u.branch_id}`);
      return isSales && sameBranch;
    });

    console.log('Found branch sales users:', branchSalesUsers);

    if (branchSalesUsers.length === 0) {
      alert('Bu şubede satış ekibi üyesi bulunamadı.');
      return;
    }

    const newUserIds = branchSalesUsers.map(u => u.id).filter(id => !formData.affectedUsers.includes(id));
    setFormData(prev => ({
      ...prev,
      affectedUsers: [...prev.affectedUsers, ...newUserIds]
    }));

    alert(`${branchSalesUsers.length} satış ekibi üyesi eklendi.`);
  };



  const addAllUniversities = () => {
    setFormData(prev => ({
      ...prev,
      relatedUniversities: [...allUniversities]
    }));
  };

  const getUserName = (userId: string) => {
    const foundUser = dbUsers.find(u => u.id === userId);
    return foundUser?.name || 'Bilinmeyen Kullanıcı';
  };



  const getTypeIcon = (type: Deadline['type']) => {
    switch (type) {
      case 'application': return GraduationCap;
      case 'document': return FileText;
      case 'payment': return DollarSign;
      case 'visa': return Plane;
      case 'enrollment': return Target;
      default: return Clock;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Insert deadline into database
      const { data: deadlineData, error: deadlineError } = await supabase
        .from('deadlines')
        .insert({
          title: formData.title,
          description: formData.description,
          type: formData.type,
          due_date: formData.dueDate,
          priority: formData.priority,
          category: formData.category,
          affected_users: formData.affectedUsers,
          created_by: user?.id,
          is_active: true,
          reminder_days: formData.reminderDays,
          related_universities: formData.relatedUniversities,
          related_countries: formData.relatedCountries,
          notes: formData.notes,
          contact_person: formData.contactPerson,
          contact_email: formData.contactEmail
        })
        .select()
        .single();

      if (deadlineError) throw deadlineError;

      // Create notifications for all affected users
      if (formData.affectedUsers.length > 0) {
        const notificationsToInsert = formData.affectedUsers.map(userId => ({
          title: `Yeni Deadline: ${formData.title}`,
          message: `${formData.description} - Son tarih: ${new Date(formData.dueDate).toLocaleDateString('tr-TR')}`,
          type: formData.priority === 'critical' ? 'error' : formData.priority === 'high' ? 'warning' : 'info',
          category: 'reminder',
          user_id: userId,
          is_read: false,
          related_user: user?.name
        }));

        const { error: notifError } = await supabase
          .from('notifications')
          .insert(notificationsToInsert);

        if (notifError) console.error('Error creating notifications:', notifError);
      }

      // Deadline notifications RLS policy eksik olduğu için şimdilik devre dışı
      // İleride RLS düzeltildiğinde aktif edilecek

      // Dispatch notification event for real-time updates
      window.dispatchEvent(new CustomEvent('newNotification', {
        detail: {
          title: `Yeni Deadline: ${formData.title}`,
          message: `${formData.description} - Son tarih: ${new Date(formData.dueDate).toLocaleDateString('tr-TR')}`,
          type: formData.priority === 'critical' ? 'error' : formData.priority === 'high' ? 'warning' : 'info',
          category: 'reminder'
        }
      }));

      // Dispatch deadline event
      window.dispatchEvent(new CustomEvent('newDeadline', { detail: deadlineData }));

      // Show success message
      alert(`Deadline başarıyla oluşturuldu! ${formData.affectedUsers.length} kullanıcıya bildirim gönderildi.`);

      onSave(deadlineData);
      onClose();
    } catch (error) {
      console.error('Error creating deadline:', error);
      alert('Deadline oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const TypeIcon = getTypeIcon(formData.type);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Yeni Deadline Oluştur</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Deadline Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Deadline Başlığı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Örn: İtalya Üniversiteleri Bahar Dönemi Başvuru Deadline"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Deadline Türü <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <TypeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="application">Başvuru</option>
                    <option value="document">Belge</option>
                    <option value="payment">Ödeme</option>
                    <option value="visa">Vize</option>
                    <option value="enrollment">Kayıt</option>
                    <option value="other">Diğer</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Son Tarih <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Öncelik <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <AlertTriangle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Düşük</option>
                    <option value="medium">Orta</option>
                    <option value="high">Yüksek</option>
                    <option value="critical">Kritik</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Kategori
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="university">Üniversite</option>
                  <option value="visa">Vize</option>
                  <option value="payment">Ödeme</option>
                  <option value="document">Belge</option>
                  <option value="general">Genel</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Açıklama <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Deadline hakkında detaylı açıklama..."
                />
              </div>
            </div>
          </div>

          {/* Affected Users */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Etkilenen Kullanıcılar</h3>

            {/* Quick Add Buttons */}
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Hızlı Ekleme:</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => addAllByRole('sales')}
                  className="px-3 py-1.5 bg-green-100 hover:bg-green-200 dark:bg-green-900/20 dark:hover:bg-green-900/30 text-green-800 dark:text-green-400 text-sm rounded-lg transition-colors"
                >
                  Tüm Satış Ekibi
                </button>
                <button
                  type="button"
                  onClick={addBranchSalesTeam}
                  className="px-3 py-1.5 bg-teal-100 hover:bg-teal-200 dark:bg-teal-900/20 dark:hover:bg-teal-900/30 text-teal-800 dark:text-teal-400 text-sm rounded-lg transition-colors"
                >
                  Şube Satış Ekibi
                </button>
                <button
                  type="button"
                  onClick={() => addAllByRole('admin')}
                  className="px-3 py-1.5 bg-red-100 hover:bg-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/30 text-red-800 dark:text-red-400 text-sm rounded-lg transition-colors"
                >
                  Tüm Yöneticiler
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const allUserIds = dbUsers.map(u => u.id).filter(id => !formData.affectedUsers.includes(id));
                    setFormData(prev => ({
                      ...prev,
                      affectedUsers: [...prev.affectedUsers, ...allUserIds]
                    }));
                  }}
                  className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-800 dark:text-blue-400 text-sm rounded-lg transition-colors"
                >
                  Tüm Kullanıcıları Ekle
                </button>
              </div>
            </div>

            {/* Search and Add Users */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Kullanıcı Ekle
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchUsers}
                  onChange={(e) => setSearchUsers(e.target.value)}
                  placeholder="Kullanıcı ara..."
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {searchUsers && (
                <div className="mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {loadingUsers ? (
                    <div className="p-4 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Kullanıcılar yükleniyor...</p>
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      Kullanıcı bulunamadı
                    </div>
                  ) : (
                    filteredUsers.map(user => (
                      <div
                        key={user.id}
                        onClick={() => addUser(user.id, user.name)}
                        className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer flex items-center justify-between"
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {user.email} • {user.role === 'admin' ? 'Yönetici' :
                              user.role === 'unit' && user.unit_type === 'sales' ? 'Satış' :
                                user.role === 'branch' ? 'Şube' :
                                  user.unit_type || user.role}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="p-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800/50"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Selected Users */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Seçilen Kullanıcılar ({formData.affectedUsers.length})
              </label>
              {formData.affectedUsers.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                  Henüz kullanıcı eklenmedi
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {formData.affectedUsers.map(userId => (
                    <div
                      key={userId}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <span className="font-medium text-gray-900 dark:text-white">{getUserName(userId)}</span>
                      <button
                        type="button"
                        onClick={() => removeUser(userId)}
                        className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>



          {/* Related Universities */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">İlgili İtalyan Üniversiteleri</h3>

            {/* Quick Add All Universities */}
            <div className="mb-4">
              <button
                type="button"
                onClick={addAllUniversities}
                className="px-4 py-2 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/20 dark:hover:bg-purple-900/30 text-purple-800 dark:text-purple-400 rounded-lg transition-colors"
              >
                Tüm İtalyan Üniversitelerini Ekle
              </button>
            </div>

            {/* Search and Add Universities */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchUniversities}
                  onChange={(e) => setSearchUniversities(e.target.value)}
                  placeholder="Üniversite ara..."
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {searchUniversities && (
                <div className="mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredUniversities.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      Üniversite bulunamadı
                    </div>
                  ) : (
                    filteredUniversities.map(university => (
                      <div
                        key={university}
                        onClick={() => addUniversity(university)}
                        className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer flex items-center justify-between"
                      >
                        <span className="font-medium text-gray-900 dark:text-white">{university}</span>
                        <button
                          type="button"
                          className="p-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full hover:bg-purple-200 dark:hover:bg-purple-800/50"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Selected Universities */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Seçilen Üniversiteler ({formData.relatedUniversities.length})
              </label>
              {formData.relatedUniversities.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                  Henüz üniversite eklenmedi
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {formData.relatedUniversities.map(university => (
                    <span
                      key={university}
                      className="flex items-center space-x-1 px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-600/20 dark:text-purple-300 text-sm rounded-full border border-purple-200 dark:border-purple-600/30"
                    >
                      <span>{university}</span>
                      <button
                        type="button"
                        onClick={() => removeUniversity(university)}
                        className="hover:text-purple-600 dark:hover:text-purple-200"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notlar
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Ek notlar..."
            />
          </div>



          {/* Reminder Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Hatırlatma Ayarları</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {formData.reminderDays.map((day) => (
                <span
                  key={day}
                  className="flex items-center space-x-1 px-3 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-600/20 dark:text-yellow-300 text-sm rounded-full border border-yellow-200 dark:border-yellow-600/30"
                >
                  <span>{day} gün önce</span>
                  <button
                    type="button"
                    onClick={() => removeReminderDay(day)}
                    className="hover:text-yellow-600 dark:hover:text-yellow-200"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex space-x-2">
              <input
                type="number"
                value={newReminderDay}
                onChange={(e) => setNewReminderDay(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addReminderDay())}
                placeholder="Gün sayısı"
                min="1"
                max="365"
                className="w-32 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={addReminderDay}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors flex items-center space-x-1"
              >
                <Plus className="w-4 h-4" />
                <span>Hatırlatma Ekle</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
              Deadline'dan kaç gün önce hatırlatma gönderileceğini belirleyin
            </p>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">İletişim Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  İletişim Kişisi
                </label>
                <input
                  type="text"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Sorumlu kişi adı"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  İletişim Email
                </label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="iletisim@pisaeducazione.com"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          {formData.title && formData.dueDate && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
              <h3 className="text-lg font-medium text-blue-900 dark:text-blue-300 mb-4">Deadline Önizleme</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">Genel Bilgiler</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-blue-700 dark:text-blue-400">Başlık:</span>
                      <span className="text-blue-900 dark:text-blue-300 font-medium">{formData.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700 dark:text-blue-400">Tarih:</span>
                      <span className="text-blue-900 dark:text-blue-300 font-medium">
                        {new Date(formData.dueDate).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700 dark:text-blue-400">Öncelik:</span>
                      <span className="text-blue-900 dark:text-blue-300 font-medium capitalize">
                        {formData.priority === 'critical' ? 'Kritik' :
                          formData.priority === 'high' ? 'Yüksek' :
                            formData.priority === 'medium' ? 'Orta' : 'Düşük'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700 dark:text-blue-400">Tür:</span>
                      <span className="text-blue-900 dark:text-blue-300 font-medium">
                        {formData.type === 'application' ? 'Başvuru' :
                          formData.type === 'document' ? 'Belge' :
                            formData.type === 'payment' ? 'Ödeme' :
                              formData.type === 'visa' ? 'Vize' :
                                formData.type === 'enrollment' ? 'Kayıt' : 'Diğer'}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">Etki Alanı</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-lg font-bold text-blue-900 dark:text-blue-300">
                        {formData.affectedUsers.length}
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-400">Kullanıcı</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-blue-900 dark:text-blue-300">
                        {formData.affectedUsers.length}
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-400">Öğrenci</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-blue-900 dark:text-blue-300">
                        {formData.relatedUniversities.length}
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-400">Üniversite</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSaving || !formData.title || !formData.dueDate}
              className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-all ${isSaving || !formData.title || !formData.dueDate
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                : 'bg-blue-600 hover:bg-blue-700 text-white hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25'
                }`}
            >
              <Save className={`w-4 h-4 ${isSaving ? 'animate-pulse' : ''}`} />
              <span>{isSaving ? 'Oluşturuluyor...' : 'Deadline Oluştur'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDeadlineModal;